/**
 * The API's failure shape, and the distinctions the UI is required to preserve.
 *
 * The backend goes to real lengths to keep "you may not" (403) separate from
 * "nobody could check" (503). Collapsing them would tell every operator their
 * access was revoked during an Okta outage, so the taxonomy below is modelled
 * explicitly rather than left to `if (status >= 400)` at each call site.
 */

/**
 * The discriminator is the `error` field, not a separate `code`. Every failure
 * schema in the contract declares `error` as a required enum, so it is the one
 * value always safe to branch on.
 */

/** `error` on a 403. These need different fixes and must not be collapsed. */
export type ForbiddenCode = 'not-an-operator' | 'level-too-low' | 'level-unreadable';

/**
 * `error` on a 403 from the member plane (`/api/me`).
 *
 * A separate type rather than a widening of {@link ForbiddenCode}, because the
 * two sets are disjoint and each plane only ever sees its own. Widening would
 * make every exhaustive switch in the console non-exhaustive over cases it can
 * never receive, and would force the console's copy table to invent wording
 * for member conditions it cannot encounter.
 *
 * Like the operator codes, these are four different problems with four
 * different fixes and must not be collapsed into one "access denied":
 *
 *  - `not-a-member` — the session names a LOCID subject that no longer exists.
 *  - `member-disabled` — the LOCID account exists and is disabled.
 *  - `discord-id-unresolved` — the account has no linked Discord id, so it owns
 *    nothing this plane can show. The member-side counterpart of
 *    `level-unreadable`: a configuration fact about the account, not a
 *    decision about the person.
 *  - `origin-mismatch` — the request did not come from the portal. Never
 *    something a member can act on, and always worth logging.
 */
export type MemberForbiddenCode =
  | 'not-a-member'
  | 'member-disabled'
  | 'discord-id-unresolved'
  | 'origin-mismatch';

/**
 * `error` on a 503. The handoff describes 503 as "Okta unreachable", but the
 * contract is broader: five different dependencies can be down, plus the
 * service restarting. All six mean "could not", never "may not".
 */
export type UnavailableCode =
  | 'oidc-provider-unavailable'
  | 'directory-unavailable'
  | 'member-directory-unavailable'
  | 'audit-storage-unavailable'
  | 'job-storage-unavailable'
  /** The service is restarting. This is what strands a job as `abandoned`. */
  | 'shutting-down';

/**
 * `error` on a 409. Every one of these is a refusal rather than a fault, but
 * they are not the same refusal: two name a state that can be changed, two
 * name a precondition that lives on another screen, and two are facts about
 * the request that no state would permit.
 */
export type ConflictCode =
  /** The LXC is locked. Unlocking it makes the operation possible. */
  | 'suspended'
  /** The LXC still has gateway bindings. They must go first. */
  | 'gateway-binds-attached'
  /** The owning hosting account is inactive. */
  | 'account-inactive'
  /** The request would shrink a disk. No state permits this. */
  | 'disk-shrink'
  /** Memory below live usage, without the acknowledgement flag set. */
  | 'dangerous-memory-reduction'
  /** The new allocation would exceed the owner's tier quota. */
  | 'quota-exceeded';

/**
 * `error` on a 409 from the member plane.
 *
 * `suspended` and `account-inactive` are shared with {@link ConflictCode} and
 * mean the same thing, but they read differently to the two audiences: an
 * operator is being told to unlock something they can unlock, and a member is
 * being told that staff have placed a hold they cannot lift themselves. The
 * two remaining codes exist only here.
 */
export type MemberConflictCode =
  /** The container is suspended. Only staff can lift the hold. */
  | 'suspended'
  /** The owning hosting account is inactive. */
  | 'account-inactive'
  /** This container already has as many subdomains as the plane permits. */
  | 'bind-limit-reached'
  /** The container has no recorded address, so nothing can be published to it. */
  | 'container-not-addressable';

/** Body every failing response carries. `opId` is a 6-hex correlation id. */
export interface ApiErrorBody {
  error: string;
  message: string;
  opId?: string;
}

/**
 * What the UI should actually branch on. Derived from status once, here, so no
 * component has to re-derive it — and so adding a case is a compile error at
 * every exhaustive switch rather than a silently missing branch.
 */
export type ApiFailureKind =
  /** 400 — the request itself was malformed. A bug here, not an operator error. */
  | 'invalid-request'
  /** 401 — no session. Send them to log in. */
  | 'unauthenticated'
  /** 403 — a real answer about this actor's authorization. Carries `code`. */
  | 'forbidden'
  /** 404 — absent. */
  | 'not-found'
  /** 409 — the target is in a state that refuses this action (e.g. suspended). */
  | 'conflict'
  /** 429 — rate limited. Carries `retryAfterSeconds` when the header was sent. */
  | 'rate-limited'
  /** 502 — Proxmox, pfSense or the gateway faulted. Not the user's fault. */
  | 'upstream'
  /**
   * 502 or 504 written by the reverse proxy rather than by the API.
   *
   * Its own member and not a widening of `upstream`, because the two say
   * opposite things about where the fault is: `upstream` means the API asked
   * Proxmox, pfSense or the gateway and got a failure back, and this means
   * nothing asked them anything — the API was not there to be asked. Telling an
   * operator that three healthy systems have failed, during an outage of the
   * one system that has, is how an incident gets chased in the wrong building.
   */
  | 'api-unreachable'
  /** 503 — a dependency is down, or we are restarting. NOT a refusal. */
  | 'unavailable'
  /** Anything else the API returned. */
  | 'server'
  /** Never reached the API at all — offline, DNS, TLS, VPN down. */
  | 'network';

export class ApiError extends Error {
  readonly kind: ApiFailureKind;
  readonly status: number;
  readonly body: ApiErrorBody | undefined;
  /** Present on 429 when the server sent `Retry-After`. */
  readonly retryAfterSeconds: number | undefined;

  constructor(init: {
    kind: ApiFailureKind;
    status: number;
    message: string;
    body?: ApiErrorBody | undefined;
    retryAfterSeconds?: number | undefined;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.body = init.body;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }

  /**
   * Correlates this response with the server logs and the audit trail. Show it
   * on every error surface — it is how support traces what happened.
   */
  get opId(): string | undefined {
    return this.body?.opId;
  }

  /** Only meaningful when `kind === 'forbidden'`. */
  get forbiddenCode(): ForbiddenCode | undefined {
    return this.kind === 'forbidden' ? (this.body?.error as ForbiddenCode | undefined) : undefined;
  }

  /** Only meaningful when `kind === 'unavailable'`. */
  get unavailableCode(): UnavailableCode | undefined {
    return this.kind === 'unavailable'
      ? (this.body?.error as UnavailableCode | undefined)
      : undefined;
  }

  /** Only meaningful when `kind === 'conflict'`. */
  get conflictCode(): ConflictCode | undefined {
    return this.kind === 'conflict' ? (this.body?.error as ConflictCode | undefined) : undefined;
  }

  /**
   * True when this failure was the service restarting. A mutation that got this
   * may still have taken effect, and an async job hit by it becomes
   * `abandoned` — so the caller must re-read state rather than simply retry.
   */
  get isShutdown(): boolean {
    return this.unavailableCode === 'shutting-down';
  }

  /**
   * A 503 means the authorization *check* failed, not that it came back
   * negative. Retrying is legitimate; retrying a 403 is not.
   */
  get isRetryable(): boolean {
    return (
      this.kind === 'unavailable' ||
      this.kind === 'upstream' ||
      // An API that is restarting or has just been redeployed is back within
      // seconds, and the request never reached it, so nothing can have been
      // half-applied by asking again.
      this.kind === 'api-unreachable' ||
      this.kind === 'rate-limited' ||
      this.kind === 'network'
    );
  }
}

/**
 * Whether a parsed body is the failure envelope the contract describes.
 *
 * Every failure schema in the contract declares `error` and `message` as
 * required strings, so a body carrying neither was not written by the API. That
 * is the only thing that separates a 502 the API sent about Proxmox from a 502
 * the proxy sent because the API was not answering — the status is the same and
 * the browser cannot tell them apart on its own.
 *
 * Deliberately a shape test rather than "did the body parse as JSON": the
 * proxy's own error page is JSON too (see
 * `deploy/platformdv.libraryofcode.org.caddy`), shaped on purpose so it cannot
 * be read as this one.
 */
function isApiEnvelope(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false;
  const { error, message } = body as Partial<ApiErrorBody>;
  return typeof error === 'string' && typeof message === 'string';
}

function kindForStatus(status: number, body: unknown): ApiFailureKind {
  switch (status) {
    case 401:
      return 'unauthenticated';
    case 400:
      return 'invalid-request';
    case 403:
      return 'forbidden';
    case 404:
      return 'not-found';
    case 409:
      return 'conflict';
    case 429:
      return 'rate-limited';
    case 502:
    case 504:
      // The API's own gateway failure explains itself in the envelope and names
      // what faulted. Anything else at these two statuses came from the proxy
      // in front of the API — which only answers for it when it cannot reach
      // it. 504 is not in the contract at all, so nothing else can produce one.
      return isApiEnvelope(body) ? 'upstream' : 'api-unreachable';
    case 503:
      return 'unavailable';
    default:
      return 'server';
  }
}

/**
 * `Retry-After` is either delta-seconds or an HTTP date. Both are legal; the
 * API sends seconds today, but parsing the date form costs three lines and
 * removes a class of "why is the countdown NaN" bug later.
 */
function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;

  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;

  const at = Date.parse(header);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

/** Builds an `ApiError` from a non-2xx `Response`, tolerating a non-JSON body. */
export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // An error page from Caddy rather than the API — a 502 with an HTML body,
    // say. Still a real failure; just without the structured fields.
    body = undefined;
  }

  // The cast above is a claim, not a check: a proxy or a WAF can answer with
  // any JSON at all. `kindForStatus` is handed the value rather than the claim,
  // and every reader of `body` has to treat its fields as unverified — see
  // `serverMessage` in `components/ApiErrorNotice.tsx`.
  const kind = kindForStatus(response.status, body);
  return new ApiError({
    kind,
    status: response.status,
    message: body?.message ?? response.statusText ?? 'Request failed',
    body,
    retryAfterSeconds:
      kind === 'rate-limited' ? parseRetryAfter(response.headers.get('Retry-After')) : undefined,
  });
}

/** Wraps a thrown fetch rejection — the request never reached the API. */
export function apiErrorFromNetwork(cause: unknown): ApiError {
  return new ApiError({
    kind: 'network',
    status: 0,
    message:
      cause instanceof Error
        ? cause.message
        : 'Could not reach the API. Check that you are on the VPN.',
  });
}
